import "./CompleteTaskModal.css";

import { Dispatch, SetStateAction } from "react";

import { TaskConstants } from "../../../../constants/workflow/TaskConstants";
import { TaskPanelData } from "../../../../models";
import { ServiceError } from "../../../../service/Shared";
import {
  ActionButtons,
  Autocomplete,
  Button,
  Column,
  DatePicker,
  FormLabel,
  Modal,
  ReadonlyText,
  Row,
  Select,
} from "../../../../widget";
import { CompleteTaskRepeatSource, useCompleteTaskModal } from "./useCompleteTaskModal";

interface CompleteTaskModalProps {
  show: boolean;
  /**
   * The task being completed — the modal submits the update itself. Omit it only alongside
   * `onSubmitResolution`, where the caller is completing several tasks and owns the updates.
   */
  task?: TaskPanelData;
  /**
   * Takes over the update: the modal collects the Resolution, hands it back and submits nothing. Its
   * promise holds the modal in its loading state, so the caller closes it once its updates settle —
   * unless it answers with repeat sources, which keep the modal open on the repeat step, one task at a
   * time, for each general task it completed (REQ-008).
   */
  onSubmitResolution?: (resolution: string) => Promise<CompleteTaskRepeatSource[] | void> | void;
  /** Overrides the task readout, for a caller completing more than one task (e.g. "3 tasks"). */
  taskLabel?: string;
  closeModal: () => void;
  closePanel: () => void;
  handleRefresh: () => void;
  setErrors?: Dispatch<SetStateAction<ServiceError[] | undefined>>;
}

export const CompleteTaskModal = ({
  show,
  task,
  onSubmitResolution,
  taskLabel,
  closeModal,
  closePanel,
  handleRefresh,
  setErrors,
}: CompleteTaskModalProps): JSX.Element => {
  const {
    handleCloseModal,
    handleSubmit,
    setResolution,
    resolution,
    isHandleSubmitLoading,

    step,
    completedTaskName,
    repeatNumber,
    repeatTotal,
    repeatDeadlineDate,
    setRepeatDeadlineDate,
    repeatAssigneeUuid,
    repeatAssigneeName,
    canAssignRepeat,
    assigneeList,
    handleAssigneeSearch,
    handleSelectAssignee,
    handleClearAssignee,
    handleSkipRepeat,
    handleCreateRepeat,
    isHandleCreateRepeatLoading,
  } = useCompleteTaskModal({
    closeModal,
    closePanel,
    handleRefresh,
    setErrors,
    task,
    onSubmitResolution,
  });

  /*
    REQ-008 — the task is already complete by this point and keeps its resolution; this step only offers
    the next instance of it. The clone carries the task's own fields, so the only inputs are the ones that
    have to be set afresh: the new deadline and, where the acting organisation owns the task, its assignee.
  */
  if (step === "repeat") {
    return (
      <Modal show={show} title="Repeat task" onClose={handleSkipRepeat}>
        <form onSubmit={(e) => handleCreateRepeat(e)}>
          <Row spacingV="l">
            <Column offset={3} span={6}>
              <p className="body2">
                This task is complete. Repeat it to create the next instance with the same details.
              </p>
            </Column>
          </Row>
          <Row spacingV="l">
            <Column offset={3} span={6}>
              {/* Which of them is being offered, when a drop completed several general tasks — each is
                  repeated on its own deadline, so the step works through them one at a time. */}
              <FormLabel label={repeatTotal > 1 ? `Task (${repeatNumber} of ${repeatTotal})` : "Task"} />
              <ReadonlyText value={completedTaskName} />
            </Column>
          </Row>
          <Row spacingV="l">
            <Column offset={3} span={6}>
              <DatePicker
                label="New deadline*"
                value={repeatDeadlineDate}
                onChange={(value) => setRepeatDeadlineDate(value ?? undefined)}
              />
            </Column>
          </Row>
          {canAssignRepeat && (
            <Row spacingV="ll">
              <Column offset={3} span={repeatAssigneeUuid ? "3_half" : 6}>
                <div className="CompleteTaskModalWrapper">
                  {/* Keyed on the queue position so each task gets a fresh picker: the Autocomplete keeps
                      its typed text internally, and clearing the stored assignee doesn't reach it — search
                      text left behind on one task would otherwise read as an assignee on the next. */}
                  <Autocomplete
                    key={repeatNumber}
                    label="New assignee"
                    placeholder="Search for user"
                    initialKey={repeatAssigneeUuid}
                    initialValue={repeatAssigneeName}
                    data={assigneeList}
                    onSelect={handleSelectAssignee}
                    onTypeSearch={handleAssigneeSearch}
                  />
                </div>
              </Column>
              {repeatAssigneeUuid && (
                <Column span="2_half">
                  <Button
                    text="Clear"
                    size="small"
                    variant="secondary"
                    onClick={handleClearAssignee}
                    className="CompleteTaskModalClearAssignee"
                  />
                </Column>
              )}
            </Row>
          )}
          <Row justify="center">
            <ActionButtons
              disabled={repeatDeadlineDate === undefined}
              onCancel={handleSkipRepeat}
              submitButtonLabel="Repeat task"
              cancelButtonLabel="No thanks"
              isLoading={isHandleCreateRepeatLoading}
            />
          </Row>
        </form>
      </Modal>
    );
  }

  return (
    <Modal show={show} title="Complete task" onClose={handleCloseModal}>
      <form onSubmit={(e) => handleSubmit(e)}>
        <Row spacingV="l">
          <Column offset={3} span={6}>
            <FormLabel label="Task" />
            <ReadonlyText value={taskLabel ?? task?.name} />
          </Column>
        </Row>
        {/* The current status, which several tasks completed together do not share — so it is shown
            only when the modal is working against one of them. */}
        {task && (
          <Row spacingV="l">
            <Column offset={3} span={6}>
              <FormLabel label="Status" />
              <ReadonlyText value={task.status} />
            </Column>
          </Row>
        )}
        <Row spacingV="ll">
          <Column offset={3} span={6}>
            <div className="CompleteTaskModalWrapper">
              <Select
                label="Resolution"
                placeholder="Select resolution"
                data={TaskConstants.RESOLUTION_SELECT_DATA}
                value={resolution}
                onChange={(key) => setResolution(key)}
              />
            </div>
          </Column>
        </Row>
        <Row justify="center">
          <ActionButtons
            disabled={resolution === undefined}
            onCancel={handleCloseModal}
            submitButtonLabel="Save"
            isLoading={isHandleSubmitLoading}
          />
        </Row>
      </form>
    </Modal>
  );
};
